import React, { useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { GridSelection } from "./gridGeometry";
import SheetCardRow, { cardHeight, CardItemData } from "./SheetCardRow";
import { CompiledView } from "./sheetTypes";

/**
 * The card layout over the same compiled view and the same list ref as the
 * grid, so search, scroll-to-match, selection and hiding rows all keep working
 * without a second implementation.
 */

interface SheetCardsProps {
  view: CompiledView;
  query: string;
  activeMatch: { rowPos: number; colPos: number } | null;
  selection: GridSelection | null;
  listRef: React.MutableRefObject<FixedSizeList | null>;
  onSelectionChange: (selection: GridSelection | null) => void;
  onCellExpand: (rowPos: number, colPos: number, anchor: HTMLElement) => void;
}

const SheetCards: React.FC<SheetCardsProps> = ({
  view,
  query,
  activeMatch,
  selection,
  listRef,
  onSelectionChange,
  onCellExpand,
}) => {
  const handleCardDown = useCallback(
    (rowPos: number, extend: boolean) => {
      const lastColumn = Math.max(0, view.columns.length - 1);
      onSelectionChange(
        extend && selection
          ? {
              ...selection,
              kind: "rows",
              anchorCol: 0,
              focusRow: rowPos,
              focusCol: lastColumn,
            }
          : {
              kind: "rows",
              anchorRow: rowPos,
              anchorCol: 0,
              focusRow: rowPos,
              focusCol: lastColumn,
            },
      );
    },
    [onSelectionChange, selection, view.columns.length],
  );

  const itemData = useMemo<CardItemData>(
    () => ({
      rows: view.rows,
      columns: view.columns,
      query,
      activeMatch,
      selection,
      onCardDown: handleCardDown,
      onValueExpand: onCellExpand,
    }),
    [
      view.rows,
      view.columns,
      query,
      activeMatch,
      selection,
      handleCardDown,
      onCellExpand,
    ],
  );

  const itemSize = cardHeight(view.columns.length);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        backgroundColor: "background.paper",
      }}
    >
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <FixedSizeList
            ref={(node) => {
              listRef.current = node;
            }}
            height={height}
            width={width}
            itemCount={view.rows.length}
            itemSize={itemSize}
            itemData={itemData}
            overscanCount={4}
          >
            {SheetCardRow}
          </FixedSizeList>
        )}
      </AutoSizer>
    </Box>
  );
};

export default SheetCards;
